/**
 * HubDemoFrame - shared chrome for single-port demo hub routes.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { ArrowLeft, Route } from "lucide-react";
import React from "react";
import { Breadcrumbs } from "../Breadcrumbs.js";
import { CopyButton } from "../CopyButton.js";
import { ProgressBar } from "../ProgressBar.js";
import { SkeletonText } from "../Skeleton.js";
import { cssProperties } from "../../styling/index.js";

export interface HubDemoFrameProps {
  readonly slug: string;
  readonly title: string;
  readonly homeHref?: string;
  readonly children: React.ReactNode;
}

export interface HubRouteLoadingProps {
  readonly title: string;
}

function pathFor(slug: string): string {
  return `/${slug}`;
}

const CSK_ROUTE_BACKGROUND =
  "repeating-linear-gradient(90deg, color-mix(in srgb, var(--csk-accent) 14%, transparent) 0 1px, transparent 1px 96px), " +
  "repeating-linear-gradient(0deg, color-mix(in srgb, var(--csk-contained) 10%, transparent) 0 1px, transparent 1px 96px), " +
  "linear-gradient(112deg, color-mix(in srgb, var(--csk-immune) 20%, var(--csk-void)) 0 20%, transparent 20% 100%), " +
  "linear-gradient(154deg, transparent 0 42%, color-mix(in srgb, var(--csk-contained) 20%, var(--csk-void)) 42% 70%, transparent 70% 100%), " +
  "linear-gradient(24deg, transparent 0 74%, color-mix(in srgb, var(--csk-inert) 16%, var(--csk-void)) 74% 100%), " +
  "var(--csk-void)";
const HUB_ROUTE_BAR_HEIGHT = "3.85rem";

export function HubRouteLoading({ title }: HubRouteLoadingProps): React.JSX.Element {
  return (
    <main aria-label={`Loading ${title}`} className="mx-auto grid max-w-[760px] gap-3.5 px-5 py-12">
      <SkeletonText lines={4} lastLineWidth="52%" ariaLabel={`Loading ${title}`} />
      <ProgressBar value={42} tone="info" ariaLabel={`${title} loading progress`} />
    </main>
  );
}

export function HubDemoFrame({
  slug,
  title,
  homeHref = "/",
  children,
}: HubDemoFrameProps): React.JSX.Element {
  const path = pathFor(slug);
  return (
    <div
      data-csk-demo-scope={slug}
      data-theme="light"
      className="isolate min-h-screen"
      style={cssProperties(
        {
          "--csk-hub-route-bar-height": HUB_ROUTE_BAR_HEIGHT,
        },
        {
          background: CSK_ROUTE_BACKGROUND,
          paddingTop: HUB_ROUTE_BAR_HEIGHT,
        },
      )}
    >
      <nav
        aria-label="Demo hub route"
        data-csk-hub-bar
        className="fixed left-0 right-0 top-0 z-[2147483000] flex items-center gap-3 overflow-hidden border-b border-[var(--hub-border,var(--csk-border))] px-3 py-2 text-[var(--csk-text)] shadow-[0_14px_34px_rgba(42,50,70,0.08)] backdrop-blur-xl"
        style={{
          height: HUB_ROUTE_BAR_HEIGHT,
          background:
            "linear-gradient(90deg, var(--csk-accent-bg), color-mix(in srgb, var(--csk-inert) 12%, transparent)), " +
            "var(--hub-nav-background, color-mix(in srgb, var(--csk-surface) 92%, transparent))",
        }}
      >
        <a
          href={homeHref}
          className="inline-flex min-h-[30px] items-center gap-1.5 rounded-lg border border-[var(--csk-accent)] bg-[var(--csk-accent-bg)] px-2.5 py-1 text-[13px] font-extrabold text-[var(--csk-bright)] no-underline transition hover:bg-[var(--csk-separate-bg)]"
        >
          <ArrowLeft aria-hidden="true" size={15} />
          All demos
        </a>
        <div className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-extrabold text-[var(--csk-bright)] sm:hidden">
            {title}
          </span>
          <Breadcrumbs
            className="hidden sm:block"
            items={[
              { id: "hub", label: "Hub", href: homeHref },
              { id: slug, label: title },
            ]}
            separator=">"
            ariaLabel="Demo hub breadcrumb"
          />
          <div className="mt-0.5 hidden flex-wrap items-center gap-2 text-[11.5px] font-semibold text-[var(--hub-muted,var(--csk-muted))] sm:flex">
            <span>{path}</span>
            <span aria-hidden="true" className="text-[var(--csk-subtle)]">
              ·
            </span>
            <span className="inline-flex items-center gap-1 text-[var(--csk-accent)]">
              <Route aria-hidden="true" size={12} />
              single-port route
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CopyButton value={path} idleLabel="Copy path" copiedLabel="Path copied" />
        </div>
      </nav>
      {children}
    </div>
  );
}
